import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { FlightConnectionsService } from 'app/services/flight-connections-services';
import { UserService } from 'app/services/user.service';
import { AppStateService } from 'app/services/app-state.service';
import { TimeAndDateService } from 'app/services/time-and-date-service';
import { FlightOptionService } from 'app/services/flight-option-service';
import { ConversationService } from 'app/services/conversation.service';
import { UserInteractionService } from 'app/services/user-interaction.service';

import { FlightSecondaryDataComponent } from 'app/components/distinct-components/flight/flight-secondary-data/flight-secondary-data.component';
import { PrimaryBtnComponent } from 'app/components/ui-kit/buttons/primary-btn/primary-btn.component';
import { SecondaryBtnComponent } from 'app/components/ui-kit/buttons/secondary-btn/secondary-btn.component';
import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { PageOverlayComponent } from 'app/components/ui-kit/page-components/page-overlay/page-overlay.component';
import { IconPromptCardComponent } from '../../cards/icon-prompt-card/icon-prompt-card.component';
import { BaseTextAreaComponent } from "app/components/ui-kit/inputs/base-text-area/base-text-area.component";

@Component({
  selector: 'app-request-connection-overlay',
  standalone: true,
  imports: [CommonModule, PageOverlayComponent, SvgLoaderComponent, FlightSecondaryDataComponent, BaseTextAreaComponent,
    PrimaryBtnComponent, SecondaryBtnComponent, BaseTextAreaComponent, FormsModule, IconPromptCardComponent],
  templateUrl: './request-connection-overlay.component.html',
  styleUrl: './request-connection-overlay.component.scss'
})
export class RequestConnectionOverlayComponent {
  @Input() userFlightOption: any = null;
  flightOption: any = null;
  timezone: string = null;
  privateName: string = null;
  linkedInStat: string = null;
  reviewStat: string = null;
  isLinkedInConnected: boolean = false;
  isReviewed: boolean = false;
  isSendingRequest: boolean = false;

  isRequestConnection: boolean = false;
  isAlreadyRequested: boolean = false;
  isRequestSent: boolean = false;
  isUserBlocked: boolean = false;
  numberOfSeats: number;
  requestMessage: string = '';
  maxSeats: number = 10;
  messageError: string = null;

  constructor(
    private appState: AppStateService,
    private timeAndDate: TimeAndDateService,
    private connectionsService: FlightConnectionsService,
    private userService: UserService,
    private flightOptionService: FlightOptionService,
    private conversationService: ConversationService,
    private userInteraction: UserInteractionService
  ) { }

  async ngOnInit(): Promise<void> {
    this.flightOption = this.appState.agent.selectedOption();
    this.privateName = this.userService.getPrivateName(this.flightOption?.creatorDisplayName || 'Tail One User'); // TODO: Investigate

    let dataModel = null;
    if (this.userFlightOption) {
      dataModel = this.userFlightOption;
    } else {
      dataModel = this.appState.agent.currentDataModel();
      const savedId = dataModel?.userFlightOptionId;
      if (savedId) {
        try {
          const existing = await this.flightOptionService.getFlightOption(savedId);
          if (existing) {
            this.userFlightOption = existing;
            dataModel = this.userFlightOption;
          }
        } catch (err) {
          console.error('Error loading user flight option', err);
        }
      }
    }

    if (dataModel?.userFlightOptionId && !this.userFlightOption) {
      this.userFlightOption = await this.flightOptionService.getFlightOption(dataModel.userFlightOptionId);
      if (this.userFlightOption) {
        dataModel = this.userFlightOption;
      }
    }

    let userMaxSeats: number | null = null;
    const optionMaxSeats = this.parsePositiveInteger(this.flightOption.aircraftMaxSeats);
    if (this.userFlightOption) {
      userMaxSeats = this.parsePositiveInteger(dataModel?.memberships?.[0]?.seatingConfiguration);
    } else {
      userMaxSeats = this.parsePositiveInteger(
        dataModel?.userData?.membership?.aircraftMaxSeats ??
        dataModel?.userData?.membership?.seatingConfiguration ??
        dataModel?.memberships?.[0]?.seatingConfiguration
      );
    }

    const availableSeatCaps = [userMaxSeats, optionMaxSeats]
      .filter((value): value is number => value !== null);

    let effectiveMaxSeats = this.maxSeats;
    if (this.flightOption.isExistingReservation) {
      effectiveMaxSeats = optionMaxSeats ?? userMaxSeats ?? this.maxSeats;
    } else if (availableSeatCaps.length > 0) {
      effectiveMaxSeats = Math.max(...availableSeatCaps);
    }

    const claimedSeats = this.flightOption.seatsNeeded || this.flightOption.seatsOffered || 0;

    this.maxSeats = Math.max(1, effectiveMaxSeats - claimedSeats);

    const requestedSeats = dataModel.seatsNeeded
      ? dataModel.seatsNeeded
      : (dataModel.reservationData?.seatsOffered || 0);
    this.numberOfSeats = Math.min(Math.max(1, requestedSeats || 1), this.maxSeats);

    this.loadStats();
    this.checkForRequests();
    this.timezone = this.timeAndDate.getTimezoneAbbreviation(this.flightOption.timeZone);
  }

  async checkForRequests() {
    const existingRequest = await this.connectionsService.checkHasUserRequestedOption(
      this.flightOption.id,
      this.flightOption.creatorId
    );
    this.isAlreadyRequested = existingRequest;
    const blockedByOther = await this.userInteraction.isUserBlockedBy(this.flightOption.creatorId);
    const hasBlocked = await this.userInteraction.hasCurrentUserBlocked(this.flightOption.creatorId);
    this.isUserBlocked = blockedByOther || hasBlocked;
  }

  async loadStats() {
    // this.linkedInStat = await this.getLinkedInStat();
    this.reviewStat = await this.getReviewStat();
  }

  // async getLinkedInStat() { // TODO: Handle real linkedIn Connections
  //   this.isLinkedInConnected = Math.random() < 0.7;
  //   return `${this.flightOption.creatorLinkedInMatrix?.length || 0} Connections`;
  // }

  async getReviewStat() { // TODO: handle real review stats
    const { averageRating, totalReviews } = await this.userService.getRatingStats(this.flightOption.creatorId);
    this.isReviewed = totalReviews > 0;
    return `${averageRating.toFixed(1)}/5 (${totalReviews} flight${totalReviews === 1 ? '' : 's'})`;
  }

  l(location) {
    return location.endsWith(', US') ? location.replace(/, US$/, '') : location;
  }

  d(date): string {
    return this.timeAndDate.formatDateToMMDDYY(date);
  }

  t(time): string {
    return this.timeAndDate.formatTimeForTimezone(this.flightOption.timezone, time);
  }

  onClickRequest() {
    this.isRequestConnection = true;
  }

  onSlideSeatsValue(event) {
    const seats = Number(event.target.value);
    this.numberOfSeats = seats;
  }

  onRequestMessageChange(value: string) {
    this.requestMessage = value;
    if (this.messageError && this.requestMessage?.trim()) {
      this.messageError = null;
    }
  }

  async onSendRequest() {
    if (this.isSendingRequest) {
      return;
    }

    const userProfile = this.appState.user.profile();
    const fromPrivateName = this.userService.getPrivateName(userProfile.displayName);
    const fromLocation = this.flightOption.departureLocationName;
    const toLocation =
      this.flightOption.arrivalLocationName ||
      this.flightOption.arrivalGeneralType ||
      this.flightOption.arrivalGeneralState ||
      this.flightOption.arrivalGeneralCountry ||
      "";

    const toUser = await this.userService.getProfileById(this.flightOption.creatorId);

    if (!toUser) {
      console.error('User no longer exists') // TODO: better frontend error;
      return;
    }

    const trimmedMessage = this.requestMessage?.trim() || '';
    if (!trimmedMessage) {
      this.messageError = 'Please include a message so they know how you would like to coordinate the flight.';
      return;
    }

    this.isSendingRequest = true;
    this.messageError = null;

    if (!this.userFlightOption) {
      try {
        const dataModel = this.appState.agent.currentDataModel();
        const newOption = await this.flightOptionService.createFlightOption(dataModel, { isVisible: false });
        this.userFlightOption = newOption;
        dataModel.userFlightOptionId = newOption.id;
        this.appState.agent.currentDataModel.set(dataModel);
        await this.conversationService.updateCurrentConversationData(
          this.appState.agent.currentConversation(),
          dataModel,
          this.appState.agent.currentIntent()
        );
      } catch (err) {
        console.error('Error creating user flight option', err);
        this.isSendingRequest = false;
        return;
      }
    }

    const requestData = {
      fromEmail: userProfile.email,
      fromDisplayName: fromPrivateName,
      fromId: userProfile?.id,
      toId: this.flightOption.creatorId,
      toEmail: toUser.email,
      fromLocation: fromLocation,
      toLocation: toLocation,
      toUserFlightOptionId: this.flightOption?.id,
      fromUserFlightOptionId: this.userFlightOption.id,
      seatsRequested: this.numberOfSeats,
      requestMessage: trimmedMessage,
    };

    try {
      await this.connectionsService.createConnectionRequest(requestData);
      this.isRequestSent = true;
    } catch (err) {
      console.error('Error sending request', err); // TODO: add frontend error
      this.isUserBlocked = true;
    } finally {
      this.isSendingRequest = false;
    }

  }

  onClickCancel() {
    this.appState.agent.selectedOption.set(null);
  }

  onClickDone() {
    this.appState.agent.selectedOption.set(null);
  }

  private parsePositiveInteger(value: any): number | null {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

}
